import React, { useState } from 'react';
import {
  CheckCircle, XCircle, ChevronDown, ChevronUp,
  Key, Shield, Globe, Database, Clock, Link2,
} from 'lucide-react';
import { getConnectorMeta } from '../../config/connectors.config';
import {
  CONNECTOR_REGISTRY,
  CONNECTOR_STATUSES,
  ENABLED_CONNECTORS,
} from '../../mock_data/connectorsMockData';

const AUTH_TYPES = [
  { value: 'api_key_header',  label: 'API Key (Header)', icon: Key,      color: 'text-blue-600',   bg: 'bg-blue-50' },
  { value: 'bearer_token',    label: 'Bearer Token',     icon: Shield,   color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'oauth2',          label: 'OAuth 2.0',        icon: Globe,    color: 'text-green-600',  bg: 'bg-green-50' },
  { value: 'service_account', label: 'Service Account',  icon: Database, color: 'text-amber-600',  bg: 'bg-amber-50' },
];

const AUTH_TYPE_MAP = Object.fromEntries(AUTH_TYPES.map((a) => [a.value, a]));

function getDefaultAuthType(connectorType, registryAuthType) {
  if (['google_analytics', 'google_search_console'].includes(connectorType)) return 'service_account';
  return registryAuthType || 'api_key_header';
}

export default function ConnectorsTab() {
  const [expanded, setExpanded] = useState({});

  const configuredConnectors = ENABLED_CONNECTORS.filter(
    (ct) => CONNECTOR_STATUSES[ct]?.connected || CONNECTOR_REGISTRY[ct]
  );

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Connector Credentials</h3>
        <p className="text-xs text-gray-500">
          Read-only view of configured data-source connectors. In production these credentials are stored
          securely and managed from the live Configurations screen.
        </p>
      </div>

      <div className="space-y-2">
        {configuredConnectors.map((ct) => (
          <ConnectorRow
            key={ct}
            connectorType={ct}
            status={CONNECTOR_STATUSES[ct]}
            registryInfo={CONNECTOR_REGISTRY[ct]}
            expanded={expanded[ct]}
            onToggle={() => setExpanded((prev) => ({ ...prev, [ct]: !prev[ct] }))}
          />
        ))}
      </div>
    </div>
  );
}

function ConnectorRow({ connectorType, status, registryInfo, expanded, onToggle }) {
  const meta = getConnectorMeta(connectorType);
  const Icon = meta.icon;
  // Resolve brand logo (Webpack returns either a string URL or {default: url})
  const logoUrl = meta.logoSrc
    ? (typeof meta.logoSrc === 'object' ? meta.logoSrc.default || meta.logoSrc : meta.logoSrc)
    : null;
  const isConfigured = status?.connected || status?.has_credentials;
  const storedCreds = status?.credentials || {};
  const storedKeys = status?.credential_keys || [];

  const defaultAuthType = getDefaultAuthType(connectorType, registryInfo?.auth_type);
  const authInfo = AUTH_TYPE_MAP[defaultAuthType] || AUTH_TYPE_MAP.api_key_header;
  const AuthIcon = authInfo.icon;

  const isOAuthConnector =
    defaultAuthType === 'oauth2' &&
    !['google_analytics', 'google_search_console'].includes(connectorType);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${logoUrl ? 'bg-white border border-gray-100' : meta.color}`}>
            {logoUrl
              ? <img src={logoUrl} alt={meta.label} className="w-6 h-6 object-contain" />
              : Icon && <Icon size={20} />}
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-gray-800">{meta.label}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-400">{registryInfo?.domain || meta.domain}</span>
              <span className="text-gray-200">·</span>
              <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${authInfo.bg} ${authInfo.color}`}>
                <AuthIcon size={10} />
                {authInfo.label}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isConfigured ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
              <CheckCircle size={12} />
              {storedKeys.length} stored
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-red-400 bg-red-50 px-2.5 py-1 rounded-full">
              <XCircle size={12} />
              Setup required
            </span>
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div className="px-4 py-3">
            {isOAuthConnector ? (
              <OAuthReadonlySection storedCreds={storedCreds} storedKeys={storedKeys} />
            ) : storedKeys.length > 0 ? (
              <div className="space-y-1.5">
                {storedKeys.map((key) => {
                  const credEntry = storedCreds[key];
                  return (
                    <div key={key} className="flex items-center justify-between bg-white border border-gray-100 rounded px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded shrink-0">{key}</span>
                        <span className="text-xs text-gray-400 font-mono truncate">{credEntry?.masked_value || '•••'}</span>
                      </div>
                      {credEntry?.updated_at && (
                        <span className="text-xs text-gray-400 flex items-center gap-1 shrink-0 ml-2">
                          <Clock size={10} />
                          {new Date(credEntry.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className="text-xs text-gray-400 mt-2 italic">
                  Read-only demo view — credentials are managed in the live app.
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-400">No credentials configured</p>
                <p className="text-xs text-gray-300 mt-1 italic">Read-only demo view</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OAuthReadonlySection({ storedCreds, storedKeys }) {
  const CONFIG_FIELDS = [
    { key: 'client_id', label: 'QB Client ID' },
    { key: 'client_secret', label: 'QB Client Secret' },
    { key: 'realm_id', label: 'Company ID (Realm)' },
    { key: 'redirect_uri', label: 'Redirect URI' },
  ];

  const hasConfig = CONFIG_FIELDS.some((f) => storedKeys.includes(f.key));
  const hasTokens = storedKeys.includes('access_token') && storedKeys.includes('refresh_token');

  return (
    <div className="space-y-4">
      {/* Step 1: OAuth App Configuration */}
      <div>
        <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
          1. OAuth App Configuration
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-3">
          {hasConfig ? (
            <div className="space-y-1.5">
              {CONFIG_FIELDS.map((f) => {
                const credEntry = storedCreds[f.key];
                if (!credEntry) return null;
                return (
                  <div key={f.key} className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{f.label}</span>
                    <span className="font-mono text-gray-400">{credEntry.masked_value}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-3">OAuth app not configured</p>
          )}
        </div>
      </div>

      {/* Step 2: Connection */}
      <div>
        <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
          2. Connection
        </div>

        {hasTokens ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              <CheckCircle size={14} className="text-green-500" />
              <span className="text-sm font-medium text-green-700">Connected</span>
              {storedCreds?.realm_id?.masked_value && (
                <span className="text-xs text-green-500 ml-auto">
                  Realm: {storedCreds.realm_id.masked_value}
                </span>
              )}
            </div>
            <div className="space-y-1">
              {['access_token', 'refresh_token'].map((key) => {
                const credEntry = storedCreds[key];
                if (!credEntry) return null;
                return (
                  <div key={key} className="flex items-center justify-between text-xs text-gray-500">
                    <span className="font-mono bg-gray-50 px-1.5 py-0.5 rounded">{key}</span>
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      {credEntry.updated_at ? new Date(credEntry.updated_at).toLocaleString() : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-3 bg-white border border-gray-100 rounded-lg">
            <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <Link2 size={12} /> Not connected
            </p>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-400 italic">
        Read-only demo view — OAuth lifecycle is managed in the live app.
      </div>
    </div>
  );
}
